import { type JSX, useMemo, useState } from 'react';
import { Conditional } from 'dg-web-shared/lib/ReactHelpers.tsx';
import { isDefined } from 'dg-web-shared/lib/MaybeV2.ts';
import * as NumberFormatter from 'dg-web-shared/lib/NumberFormatter.ts';
import { Formatter } from 'dg-web-shared/lib/Date.ts';
import {
    MainContainer,
    MainContentBox,
} from '../../shared-mui-components/MainContent.tsx';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from '@mui/material';
import { AccountBalance, Download, PictureAsPdf } from '@mui/icons-material';
import { NoPermission } from '../../common/components/NoPermission.tsx';
import * as Http from '../../api/Http.ts';
import { css } from '@emotion/css';
import { ColorHex } from '../../ui/Colors.ts';
import { EmDash } from 'dg-web-shared/lib/Punctuation.ts';
import { Localized, useLanguage } from 'dg-web-shared/lib/Localization.tsx';
import { Message } from 'dg-web-shared/lib/Localized.ts';
import { MaterialHeader } from '../../layout/components/MaterialHeader.tsx';
import { MaterialTitle } from '../../layout/components/MaterialTitle.tsx';
import { LocalizedOverflow } from '../../layout/components/LocalizedOverflow.tsx';
import {
    FilterConfig,
    FilterDateRangeField,
} from '../../shared-mui-components/filter/OperatorFilterHelpers.tsx';
import { BackendFilterType } from '../../shared-mui-components/filter/OperatorFilterBackendConfiguration.tsx';
import { FiltersDropdown } from '../../parkingabo-users/OperatorParkingaboUsersFilterList.tsx';
import { FilterBar } from '../../shared-mui-components/filter/FiltersBox.tsx';
import { DateTime } from 'luxon';
import { useOperatorContext } from '../../app/components/BaseLayoutAndData.tsx';
import { OperatorAsyncLoadedSection } from '../../app/components/OperatorAsyncLoadedSection.tsx';
import {
    RequestStatus,
    useServerFetch,
} from 'dg-web-shared/lib/hooks/ServerStateHooks.ts';
import Alert from '@mui/material/Alert';
import { urlWithOperatorExternalId } from '../../utils/Utils.ts';

type AuditReportType = 'NONE' | 'PDF' | 'CSV';
type FilterState = {
    monthFilterActive: boolean;
    fromFilter: DateTime;
    toFilter: DateTime;
};
type BaseReport = {
    transactions: number;
    payStationTerminalPayments: number;
    permits: number;
    parkingAboProducts: number;
    refund: number;
    refundCount: number;
    comission: number;
    other: number;
    payout: number;
    saldo: number;
};
type BaseActivityMonth = BaseReport & {
    specialReport: AuditReportType;
    hasEndOfMonthReport: boolean;
    contractVersion: Message;
    dataDeleted: boolean;
};
type ActivityMonthInput = BaseActivityMonth & {
    period: string;
};
type ActivityMonth = BaseActivityMonth & {
    period: DateTime;
};
const DATE_RANGE_FILTER_KEY = 'activityPeriod';
const LEGACY_PDF_CUTOFF = DateTime.fromISO('2017-12-01').startOf('day');
const TABLE_ROW_HEIGHT = '60px';

export function ActivityTableHeader({
    auditReportColumn,
    includesParkingAboProducts,
    includesTerminalPayments,
}: {
    auditReportColumn: AuditReportType;
    includesParkingAboProducts: boolean;
    includesTerminalPayments: boolean;
}) {
    return (
        <TableHead>
            <TableRow>
                <TableCell>
                    <Localized de="Monat" fr="Mois" it="Mese" en="Month" />
                </TableCell>
                <TableCell align="right">
                    <Localized
                        de="Parkvorgänge"
                        fr="Procédures"
                        it="Procedure"
                        en="Transactions"
                    />
                </TableCell>
                {includesTerminalPayments && (
                    <TableCell
                        align="right"
                        sx={{
                            maxWidth: 130,
                            whiteSpace: 'nowrap',
                        }}
                    >
                        <LocalizedOverflow
                            de="Terminal-Zahlungen"
                            fr="Paiements par terminal"
                            it="Pagamenti con terminale"
                            en="Terminal payments"
                        />
                    </TableCell>
                )}
                <TableCell align="right">
                    <Localized
                        de="Bewilligungen"
                        fr="Autorisations"
                        it="Autorizzazioni"
                        en="Permits"
                    />
                </TableCell>
                <Conditional c={includesParkingAboProducts}>
                    <TableCell align="right">
                        <Localized
                            de="Produkte"
                            fr="Produits"
                            it="Prodotti"
                            en="Products"
                        />
                    </TableCell>
                </Conditional>
                <TableCell align="right" sx={{ maxWidth: 130 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        <LocalizedOverflow
                            de="Rückerstattungen"
                            fr="Remboursements"
                            it="Rimborsi"
                            en="Refunds"
                        />
                        <DownloadSpacer />
                    </Box>
                </TableCell>
                <TableCell align="right" sx={{ maxWidth: 170 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        <LocalizedOverflow
                            de="Kommissionen (inkl. MwSt.)"
                            fr="Commissions (TVA incl.)"
                            it="Commissioni (IVA incl.)"
                            en="Commissions (incl. VAT)"
                        />
                        <DownloadSpacer />
                    </Box>
                </TableCell>
                <TableCell align="right">
                    <Localized
                        de="Version"
                        fr="Version"
                        it="Versione"
                        en="Version"
                    />
                </TableCell>
                <TableCell align="right">
                    <Localized
                        de="Sonstige"
                        fr="Autre"
                        it="Altro"
                        en="Others"
                    />
                </TableCell>
                <TableCell align="right">
                    <Localized
                        de="Überweisung"
                        fr="Virement"
                        it="Bonifico"
                        en="Outpayment"
                    />
                </TableCell>
                <TableCell align="right">
                    <Localized de="Saldo" fr="Solde" it="Saldo" en="Saldo" />
                </TableCell>
                <TableCell align="right">
                    <Localized de="PDF" fr="PDF" it="PDF" en="PDF" />
                </TableCell>
                <Conditional c={auditReportColumn !== 'NONE'}>
                    <TableCell align="right">
                        <Localized
                            de="Audit"
                            fr="Audit"
                            it="Audit"
                            en="Audit"
                        />
                    </TableCell>
                </Conditional>
            </TableRow>
        </TableHead>
    );
}

const monthSum = (months: ActivityMonth[]): BaseReport => {
    const acc = {
        transactions: 0,
        payStationTerminalPayments: 0,
        permits: 0,
        parkingAboProducts: 0,
        refund: 0,
        refundCount: 0,
        comission: 0,
        other: 0,
        payout: 0,
        saldo: 0,
    };
    const FLOAT_DIV = 1000;
    for (const m of months) {
        acc.transactions += m.transactions * FLOAT_DIV;
        acc.payStationTerminalPayments +=
            m.payStationTerminalPayments * FLOAT_DIV;
        acc.permits += m.permits * FLOAT_DIV;
        acc.parkingAboProducts += m.parkingAboProducts * FLOAT_DIV;
        acc.comission += m.comission * FLOAT_DIV;
        acc.other += m.other * FLOAT_DIV;
        acc.payout += m.payout * FLOAT_DIV;

        if (!currentMonth(m.period)) {
            acc.refund += m.refund;
            acc.refundCount += m.refundCount;
        }
    }
    acc.transactions /= FLOAT_DIV;
    acc.payStationTerminalPayments /= FLOAT_DIV;
    acc.permits /= FLOAT_DIV;
    acc.parkingAboProducts /= FLOAT_DIV;
    acc.comission /= FLOAT_DIV;
    acc.other /= FLOAT_DIV;
    acc.payout /= FLOAT_DIV;
    return acc;
};

const currentMonth = (month: DateTime) =>
    month.month === DateTime.now().month && month.year === DateTime.now().year;

const formatMonthAbbrevYear = (month: DateTime, lang: string) =>
    `${Formatter.monthNameAbbr(month, lang)} ${month
        .setLocale(lang)
        .toFormat('yyyy')}`;

const getPeriodString = (month: DateTime, lang: string) => {
    if (currentMonth(month)) {
        return {
            de: 'laufender Monat',
            fr: 'mois courant',
            it: 'mese corrente',
            en: 'current month',
        }[lang];
    }
    return formatMonthAbbrevYear(month, lang);
};

export function Row({
    month,
    language,
    includesParkingAboProducts,
    includesTerminalPayments,
}: {
    month: ActivityMonth;
    language: string;
    includesParkingAboProducts: boolean;
    includesTerminalPayments: boolean;
}): JSX.Element {
    const { operatorExternalId } = useOperatorContext();
    return (
        <TableRow
            hover
            sx={{
                height: TABLE_ROW_HEIGHT,
            }}
        >
            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                {getPeriodString(month.period, language)}
            </TableCell>
            <TableCell align="right">
                {month.dataDeleted
                    ? EmDash
                    : NumberFormatter.numberToPrice(month.transactions, false)}
            </TableCell>
            {includesTerminalPayments && (
                <TableCell align="right">
                    {month.dataDeleted
                        ? EmDash
                        : NumberFormatter.numberToPrice(
                              month.payStationTerminalPayments,
                              false,
                          )}
                </TableCell>
            )}
            <TableCell align="right">
                {month.dataDeleted
                    ? EmDash
                    : NumberFormatter.numberToPrice(month.permits, false)}
            </TableCell>
            <Conditional c={includesParkingAboProducts}>
                <TableCell align="right">
                    {month.dataDeleted
                        ? EmDash
                        : NumberFormatter.numberToPrice(
                              month.parkingAboProducts,
                              false,
                          )}
                </TableCell>
            </Conditional>
            <TableCell align="right">
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        whiteSpace: 'nowrap',
                    }}
                >
                    {!currentMonth(month.period) && !!month.refundCount ? (
                        <>
                            {NumberFormatter.numberToPrice(month.refund, false)}
                            {` (${month.refundCount})`}
                            <DownloadLink
                                href={urlWithOperatorExternalId(
                                    `/ui-api/operator-account/refund-export/${month.period.toFormat(
                                        'yyyy-MM',
                                    )}?language=${language}`,
                                    operatorExternalId,
                                )}
                            />
                        </>
                    ) : (
                        <>
                            {EmDash}
                            <DownloadSpacer />
                        </>
                    )}
                </Box>
            </TableCell>
            <TableCell align="right">
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        whiteSpace: 'nowrap',
                    }}
                >
                    {month.dataDeleted || currentMonth(month.period)
                        ? EmDash
                        : NumberFormatter.numberToPrice(month.comission, false)}
                    {month.dataDeleted || currentMonth(month.period) ? (
                        <DownloadSpacer />
                    ) : (
                        <DownloadLink
                            href={urlWithOperatorExternalId(
                                `/ui-api/operator-account/commission-export/${month.period.toFormat(
                                    'yyyy-MM',
                                )}?language=${language}`,
                                operatorExternalId,
                            )}
                        />
                    )}
                </Box>
            </TableCell>
            <TableCell align="right" sx={{ maxWidth: 0 }}>
                <LocalizedOverflow {...month.contractVersion} />
            </TableCell>
            <TableCell align="right">
                {month.dataDeleted
                    ? EmDash
                    : NumberFormatter.numberToPrice(month.other, false)}
            </TableCell>
            <TableCell align="right">
                <b>
                    {month.dataDeleted || currentMonth(month.period)
                        ? EmDash
                        : NumberFormatter.numberToPrice(month.payout, false)}
                </b>
            </TableCell>
            <TableCell align="right">
                <b>
                    {month.dataDeleted || currentMonth(month.period)
                        ? EmDash
                        : NumberFormatter.numberToPrice(month.saldo, false)}
                </b>
            </TableCell>
            <TableCell align="right">
                <Conditional c={month.hasEndOfMonthReport}>
                    <a
                        target="_blank"
                        className={css({
                            textDecoration: 'none',
                            color: ColorHex.darkblue,
                        })}
                        href={urlWithOperatorExternalId(
                            month.period < LEGACY_PDF_CUTOFF
                                ? Http.OperatorAccount.ActivityStatement.pdfUrl(
                                      Formatter.isoYearMonth(month.period),
                                  )
                                : Http.OperatorAccount.ActivityStatement.newPdfUrl(
                                      month.period.year,
                                      month.period.month,
                                  ),
                            operatorExternalId,
                        )}
                        rel="noreferrer"
                    >
                        <PictureAsPdf
                            sx={{
                                color: theme => theme.palette.primary.main,
                                '&:hover': {
                                    color: theme => theme.palette.blue.electric,
                                },
                            }}
                        />
                    </a>
                </Conditional>
            </TableCell>
            <Conditional c={month.specialReport !== 'NONE'}>
                <TableCell align="right">
                    <a
                        target="_blank"
                        className="AcitvityStatementPdfLink"
                        href={urlWithOperatorExternalId(
                            Http.OperatorAccount.ActivityStatement.auditReport(
                                month.specialReport,
                                month.period.year,
                                month.period.month,
                            ),
                            operatorExternalId,
                        )}
                        rel="noreferrer"
                    >
                        <Download
                            sx={theme => ({
                                color: theme.palette.primary.main,
                                '&:hover': {
                                    color: theme.palette.blue.electric,
                                },
                            })}
                        />
                    </a>
                </TableCell>
            </Conditional>
        </TableRow>
    );
}

export function ActivityTableBody({
    auditReportColumn,
    includesParkingAboProducts,
    includesTerminalPayments,
    filterActive,
    filter,
    activityMonthsData,
}: {
    auditReportColumn: AuditReportType;
    includesParkingAboProducts: boolean;
    includesTerminalPayments: boolean;
    filterActive: boolean;
    filter: FilterState;
    activityMonthsData: ActivityMonth[];
}) {
    const { language } = useLanguage();
    const data = activityMonthsData.filter((m): boolean => {
        if (!filterActive) {
            return true;
        }
        const { fromFilter, toFilter } = filter;
        if (isDefined(fromFilter) && m.period < fromFilter) {
            return false;
        }
        if (isDefined(toFilter) && m.period > toFilter) {
            return false;
        }
        return true;
    });
    const sum = monthSum(data);
    return (
        <TableBody>
            <TableRow
                sx={{
                    color: theme => theme.palette.primary.contrastText,
                    backgroundColor: theme => theme.palette.primary.main,
                    height: TABLE_ROW_HEIGHT,
                }}
            >
                <TableCell
                    sx={{
                        color: 'inherit',
                    }}
                >
                    <b>
                        <Localized
                            de="Gesamt"
                            fr="Global"
                            it="Globale"
                            en="Total"
                        />
                    </b>
                </TableCell>
                <TableCell align="right" sx={{ color: 'inherit' }}>
                    <b>
                        {NumberFormatter.numberToPrice(sum.transactions, false)}
                    </b>
                </TableCell>
                {includesTerminalPayments && (
                    <TableCell align="right" sx={{ color: 'inherit' }}>
                        <b>
                            {NumberFormatter.numberToPrice(
                                sum.payStationTerminalPayments,
                                false,
                            )}
                        </b>
                    </TableCell>
                )}
                <TableCell align="right" sx={{ color: 'inherit' }}>
                    <b>{NumberFormatter.numberToPrice(sum.permits, false)}</b>
                </TableCell>
                <Conditional c={includesParkingAboProducts}>
                    <TableCell align="right" sx={{ color: 'inherit' }}>
                        <b>
                            {NumberFormatter.numberToPrice(
                                sum.parkingAboProducts,
                                false,
                            )}
                        </b>
                    </TableCell>
                </Conditional>
                <TableCell align="right" sx={{ color: 'inherit' }}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        <b>
                            {sum.refund > 0 || sum.refundCount > 0
                                ? `${NumberFormatter.numberToPrice(
                                      sum.refund,
                                      false,
                                  )} (${sum.refundCount})`
                                : EmDash}
                        </b>
                        <DownloadSpacer />
                    </Box>
                </TableCell>
                <TableCell align="right" sx={{ color: 'inherit' }}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        <b>
                            {NumberFormatter.numberToPrice(
                                sum.comission,
                                false,
                            )}
                        </b>
                        <DownloadSpacer />
                    </Box>
                </TableCell>
                <TableCell align="right" sx={{ color: 'inherit' }} />
                <TableCell align="right" sx={{ color: 'inherit' }}>
                    <b>{NumberFormatter.numberToPrice(sum.other, false)}</b>
                </TableCell>
                <TableCell align="right" sx={{ color: 'inherit' }}>
                    <b>{NumberFormatter.numberToPrice(sum.payout, false)}</b>
                </TableCell>
                <TableCell align="right" sx={{ color: 'inherit' }} />
                <TableCell align="right" sx={{ color: 'inherit' }} />
                <Conditional c={auditReportColumn !== 'NONE'}>
                    <TableCell align="right" sx={{ color: 'inherit' }} />
                </Conditional>
            </TableRow>
            {data.map(m => (
                <Row
                    month={m}
                    language={language}
                    key={m.period.toFormat('yyyy-MM')}
                    includesParkingAboProducts={includesParkingAboProducts}
                    includesTerminalPayments={includesTerminalPayments}
                />
            ))}
        </TableBody>
    );
}

function DownloadLink(props: { href: string }) {
    return (
        <a
            target="_blank"
            className={css({
                lineHeight: 0,
            })}
            href={props.href}
            rel="noreferrer"
        >
            <Download
                sx={theme => ({
                    paddingLeft: '4px',
                    color: theme.palette.primary.main,
                    '&:hover': {
                        color: theme.palette.blue.electric,
                    },
                })}
            />
        </a>
    );
}

function DownloadSpacer() {
    return <Box sx={{ paddingX: '12px' }} />;
}

export function ActivityStatement() {
    const { settings, currentLogin } = useOperatorContext();
    const [filter, setFilter] = useState<FilterState>({
        monthFilterActive: true,
        fromFilter: DateTime.now()
            .startOf('month')
            .minus({ years: 1 })
            .startOf('year'),
        toFilter: DateTime.now().startOf('month'),
    });
    const [activityStatementState] = useServerFetch<
        ActivityMonthInput[],
        object
    >(() => ({ url: '/ui-api/operator-account/activity-statement' }), {});
    const activityMonthsData = useMemo<ActivityMonth[]>(() => {
        if (!Array.isArray(activityStatementState.data)) {
            return [];
        }
        return activityStatementState.data.map(month => ({
            ...month,
            period: DateTime.fromFormat(month.period, 'yyyy-MM').startOf(
                'month',
            ),
        }));
    }, [activityStatementState]);

    if (!currentLogin.permissions.activityStatement) {
        return <NoPermission />;
    }

    const specialReportColumn =
        activityMonthsData.length > 0
            ? activityMonthsData[0].specialReport
            : 'NONE';
    const includesParkingAboProducts =
        activityMonthsData.reduce(
            (sum, activityMonth) => sum + activityMonth.parkingAboProducts,
            0,
        ) > 0.0;
    const includesTerminalPayments =
        activityMonthsData.reduce(
            (sum, activityMonth) =>
                sum + activityMonth.payStationTerminalPayments,
            0,
        ) > 0.0;
    const periodText = filter.monthFilterActive
        ? formatMonthAbbrevYear(filter.fromFilter, settings.language) +
          ' - ' +
          formatMonthAbbrevYear(filter.toFilter, settings.language)
        : '';
    const filterConfig: FilterConfig = {
        [DATE_RANGE_FILTER_KEY]: {
            type: BackendFilterType.DATE_RANGE,
            displayName: {
                de: 'Zeitraum',
                fr: 'Période',
                it: 'Periodo',
                en: 'Period',
            },
            disableSelectionInFuture: true,
            monthSelection: true,
            fromValue: null,
            toValue: null,
        },
    };
    const activeFilters: FilterConfig = filter.monthFilterActive
        ? {
              [DATE_RANGE_FILTER_KEY]: {
                  type: BackendFilterType.DATE_RANGE,
                  displayName: filterConfig[DATE_RANGE_FILTER_KEY].displayName,
                  disableSelectionInFuture: true,
                  monthSelection: true,
                  fromValue: filter.fromFilter,
                  toValue: filter.toFilter,
              },
          }
        : {};

    return (
        <MainContainer sx={{ width: '100%', maxWidth: undefined }}>
            {activityStatementState.data?.find(m => m.period === '2026-01')
                ?.payout != null && (
                <Alert severity="info" sx={{ mx: 0, mt: 0 }}>
                    <Localized
                        de="Neu werden die Rückerstattungen nicht mehr als Abzug von den Einnahmen dargestellt, sondern separat in der entsprechenden Spalte angezeigt."
                        fr="Désormais, les remboursements ne sont plus représentés en diminution des recettes, mais sont indiqués séparément dans la colonne correspondante."
                        it="Da subito i rimborsi non vengono più rappresentati in diminuzione dalle entrate, ma vengono indicati separatamente nella colonna corrispondente."
                        en="From now on, refunds will no longer be shown as a reduction in revenue, but will be indicated separately in the corresponding column."
                    />
                </Alert>
            )}

            <MainContentBox>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        height: '100%',
                        position: 'relative',
                    }}
                >
                    <MaterialHeader editMode={false}>
                        <MaterialTitle
                            Icon={AccountBalance}
                            title={
                                <Localized
                                    de="Kontoauszug"
                                    fr="Relevé de compte"
                                    it="Estratto conto"
                                    en="Account statement"
                                />
                            }
                        />
                    </MaterialHeader>

                    <FilterBar>
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                flexGrow: 1,
                            }}
                        >
                            <FiltersDropdown
                                activeFilters={activeFilters}
                                filterConfig={filterConfig}
                                onFiltersStateChange={activeFilters => {
                                    const dateRangeFilter = activeFilters[
                                        DATE_RANGE_FILTER_KEY
                                    ] as FilterDateRangeField | undefined;
                                    if (!dateRangeFilter) {
                                        setFilter(s => ({
                                            ...s,
                                            monthFilterActive: false,
                                        }));
                                        return;
                                    }
                                    if (
                                        !dateRangeFilter.fromValue ||
                                        !dateRangeFilter.toValue ||
                                        !dateRangeFilter.fromValue.isValid ||
                                        !dateRangeFilter.toValue.isValid
                                    ) {
                                        setFilter(s => ({
                                            ...s,
                                            monthFilterActive: true,
                                        }));
                                        return;
                                    }
                                    const fromFilter =
                                        dateRangeFilter.fromValue.startOf(
                                            'month',
                                        );
                                    const toFilter =
                                        dateRangeFilter.toValue.startOf(
                                            'month',
                                        );
                                    const [fromMin, toMax] =
                                        fromFilter <= toFilter
                                            ? [fromFilter, toFilter]
                                            : [toFilter, fromFilter];
                                    setFilter({
                                        monthFilterActive: true,
                                        fromFilter: fromMin,
                                        toFilter: toMax,
                                    });
                                }}
                                pending={
                                    activityStatementState.status ===
                                    RequestStatus.PENDING
                                }
                            />
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    color: theme => theme.palette.blue.main,
                                    fontSize: 14,
                                    fontWeight: 600,
                                }}
                            >
                                {periodText}
                            </Box>
                        </Box>
                    </FilterBar>
                    <TableContainer sx={{ flex: 1 }}>
                        <OperatorAsyncLoadedSection
                            requestState={activityStatementState}
                            render={() => (
                                <Table stickyHeader>
                                    <ActivityTableHeader
                                        auditReportColumn={specialReportColumn}
                                        includesParkingAboProducts={
                                            includesParkingAboProducts
                                        }
                                        includesTerminalPayments={
                                            includesTerminalPayments
                                        }
                                    />
                                    <ActivityTableBody
                                        auditReportColumn={specialReportColumn}
                                        includesParkingAboProducts={
                                            includesParkingAboProducts
                                        }
                                        includesTerminalPayments={
                                            includesTerminalPayments
                                        }
                                        filterActive={filter.monthFilterActive}
                                        filter={filter}
                                        activityMonthsData={activityMonthsData}
                                    />
                                </Table>
                            )}
                        />
                    </TableContainer>
                </Box>
            </MainContentBox>
        </MainContainer>
    );
}
